import { format, parseISO } from 'date-fns';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, { useCallback, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { PropertiesForm } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/manual-type-properies/controlled-property-form/properties-form';
import {
    FormSection,
    FormSectionType,
    MappedFileData,
    type PropertyFormValues,
    RequestPropertyFormProps,
    ResolverErrorCodesEnum,
} from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/types';
import { UploadFileRequestProperty } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/file-based-request-property/upload-file-request-property';
import { FileFieldsMapping } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/file-based-request-property/file-fields-mapping';
import { SupportRequestsCoverageCheckModal } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/coverage-check/coverage-check';
import { SupportRequestsResultsModal } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/support-request-results-modal/support-request-results-modal';
import { useGetSubmitPropertyForm } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/use-get-submit-property-form/use-get-submit-property-form';
import { mapCsvColumnsToRequiredFields } from 'features/offline-reports/support-requests/components/support-requests-modals/utils/map-csv-columns-to-required-fields/map-csv-columns-to-required-fields';
import {
    useCreateResolverJobMutation,
    useResolverJobResultsQuery,
    useUpdateResolverJobMutation,
} from 'features/offline-reports/support-requests/components/support-requests-modals/queries/resolver-bridge-queries';
import { ModalManager } from '@placer-ui/components';
import { transformTableDataToCsv } from 'features/offline-reports/support-requests/components/support-requests-modals/utils/utils';
import {
    generateCsvFileName,
    generateResolverDataToProcessingCsvFile,
    sumItemsByStatus,
} from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/support-request-results-modal/utils';
import { supportApi } from 'API/offline-reports-api';
import { getFeatureFlags } from 'core/flow-control';
import { useTableConfig } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/support-request-results-modal/use-table-config';
import {
    dataForPropertySubmitStore,
    deliveryETAStore,
    isSubmittingTableRequestsStore,
    isUpdateTagEnabledStore,
    mappedFileDataStore,
    resolverJobStore,
    resultsTableEditedStore,
    supportRequestOriginalFileStore,
    supportTableTagIdStore,
} from 'features/offline-reports/support-requests/store/property-store';
import { useSendSupportSplunkEvent } from 'features/offline-reports/support-requests/hooks/use-send-support-splunk-event';
import { SupportRequestsSuccessContent } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/support-success-modal/support-success-modal-content';
import type { SupportRequestsSuccessModalProps } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/support-success-modal/types';
import type { SupportInputType } from 'features/offline-reports/support-requests/components/support-requests-modals/types';
import { useSubmitRequestFlowMutation } from 'features/offline-reports/support-requests/components/support-requests-modals/queries/submit-request-flow-mutation';
import { useSupportResultsTableData } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/use-support-results-table-data';
import { useOfflineReportsActions } from 'store/offline-reports/use-offline-reports-actions';
import { useTableTagsModals } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/support-request-results-modal/use-table-tags-modals';

const useHandleDoneSubmitProcess = () => {
    const sendSplunkEvent = useSendSupportSplunkEvent();
    const { getSupportRequests } = useOfflineReportsActions();
    const resolverJob = useAtomValue(resolverJobStore);
    const mappedFileData = useAtomValue(mappedFileDataStore);
    const dataForPropertySubmit = useAtomValue(dataForPropertySubmitStore);
    const supportTableTagId = useAtomValue(supportTableTagIdStore);
    const supportRequestOriginalFile = useAtomValue(supportRequestOriginalFileStore);
    const modalManager = ModalManager.getInstance();
    const { enable_resolver_run_automation_ff, enable_poi_upload_save_as_tag } = getFeatureFlags();
    const setDeliveryETA = useSetAtom(deliveryETAStore);
    const isDataEdited = useAtomValue(resultsTableEditedStore);
    const isUpdateTagEnabled = useAtomValue(isUpdateTagEnabledStore);
    const [isSubmittingTableRequests, setIsSubmittingTableRequests] = useAtom(
        isSubmittingTableRequestsStore,
    );

    const { mutateAsync: submitRequestFlowMutation, isLoading: isLoadingSubmitRequestFlow } =
        useSubmitRequestFlowMutation();
    const { mutateAsync: updateResolverJobMutation } = useUpdateResolverJobMutation();

    const { allTableData: dataTable } = useSupportResultsTableData();

    const { getColumnConfig } = useTableConfig({
        isSubmitting: false,
    });

    const columns = useMemo(
        () => getColumnConfig(mappedFileData?.matching),
        [getColumnConfig, mappedFileData?.matching],
    );

    const { statusMap, statusList } = useMemo(() => sumItemsByStatus(dataTable), [dataTable]);

    const onlySensitiveLocation = statusList.length === 1 && !!statusMap['SensitiveLocation'];
    const onlyAvailableStatus = statusList.length === 1 && !!statusMap['Available'];
    const showDoneButton = onlyAvailableStatus || onlySensitiveLocation;

    const forceSubmit = useRef({
        'continue-with-missing-info': false,
        'save-as-tag': false,
        'update-tag': false,
    });

    const handleSkipModal = (
        type: string,
        onNextHandler: RequestPropertyFormProps['onNextHandler'],
    ) => {
        modalManager.removeModal(type);
        forceSubmit.current = { ...forceSubmit.current, [type]: true };
        handleSubmit({ onNextHandler });
    };

    const { showMissingInfoModal, showSaveAsTagModal, showUpdateTagModal } = useTableTagsModals({
        onSkipModal: handleSkipModal,
    });

    const handleSubmit = async ({ onNextHandler }: Partial<RequestPropertyFormProps>) => {
        const shouldShowSaveTagModal =
            enable_poi_upload_save_as_tag &&
            !forceSubmit.current['save-as-tag'] &&
            !supportTableTagId;

        const shouldShowUpdateTagModal =
            enable_poi_upload_save_as_tag &&
            !forceSubmit.current['update-tag'] &&
            isUpdateTagEnabled;

        const shouldShowMissingInfoModal =
            !forceSubmit.current['continue-with-missing-info'] && statusMap['MissingInfo'];

        if (showDoneButton) {
            if (shouldShowSaveTagModal) {
                showSaveAsTagModal(onNextHandler);
                return true;
            }

            if (shouldShowUpdateTagModal) {
                showUpdateTagModal(onNextHandler);
                return true;
            }

            sendSplunkEvent({
                action: 'done-support-request',
                value: 'property',
                report_config: statusMap,
            });

            onNextHandler?.();
            return true;
        }

        if (shouldShowMissingInfoModal) {
            showMissingInfoModal(onNextHandler);
            return true;
        }

        if (shouldShowSaveTagModal) {
            showSaveAsTagModal(onNextHandler);
            return true;
        }

        if (shouldShowUpdateTagModal) {
            showUpdateTagModal(onNextHandler);
            return true;
        }

        setIsSubmittingTableRequests(true);

        sendSplunkEvent({
            action: 'submit-support-request',
            value: 'property',
            report_config: statusMap,
        });

        const onlyProcessingFailed = dataTable?.filter(
            (item) => item.status === 'ProcessingFailed',
        );

        const allWithoutProcessingFailedAndMissingInfo = dataTable?.filter(
            (item) => !['ProcessingFailed', 'MissingInfo'].includes(item.status),
        );

        const actionRequiredCount = dataTable?.filter(
            (item) =>
                ['ReviewRequired'].includes(item.status) &&
                [
                    ResolverErrorCodesEnum.NotAvailableVenue,
                    ResolverErrorCodesEnum.ShadowVenue,
                    ResolverErrorCodesEnum.UnResolvedVenue,
                ].includes(item.field_error_code),
        );

        const promises = [];

        const { fileData } = transformTableDataToCsv({
            columns,
            data: allWithoutProcessingFailedAndMissingInfo,
            includeChallengeItems: true,
        });

        const allItemsFile: File[] = [
            new File(
                [fileData],
                generateCsvFileName({ name: 'property_list', withTimeFormat: true }),
            ),
        ];

        const originalUploadedFile: File[] = supportRequestOriginalFile?.file
            ? [supportRequestOriginalFile.file]
            : [];

        const failedItemsFile = generateResolverDataToProcessingCsvFile(
            onlyProcessingFailed,
            mappedFileData?.matching,
        );

        promises.push(
            supportApi.submitRequest({
                type: 'property',
                files: allItemsFile,
                feedFiles: originalUploadedFile,
                ...dataForPropertySubmit,
                unmatchedProperties: statusMap['ReviewRequired'] ?? 0,
                isFailedIndication: false,
                actionRequiredCount: actionRequiredCount?.length ?? 0,
                withTag: !!supportTableTagId,
            }),
        );

        if (onlyProcessingFailed?.length) {
            promises.push(
                supportApi.submitRequest({
                    type: 'property',
                    files: failedItemsFile,
                    ...dataForPropertySubmit,
                    unmatchedProperties: 0,
                    isFailedIndication: true,
                }),
            );
        }

        // TODO - handle errors
        const res = await Promise.all(promises);

        let job_id = resolverJob?.job_id;

        if (isDataEdited) {
            const createJobResponse = await updateResolverJobMutation({
                itemIds: allWithoutProcessingFailedAndMissingInfo?.map(({ id }) => id),
                shouldRunStatus: false,
                addIsLoading: false,
            });

            job_id = createJobResponse?.job?.job_id;
        }

        const case_id = res[0].case_id;

        // TODO - need to think on better logic
        setDeliveryETA(res[0].case_data?.deliveryETA);

        if (enable_resolver_run_automation_ff && case_id && job_id) {
            await submitRequestFlowMutation({
                endpoint: 'automation-for-resolver',
                case_id,
                job_id,
                tag_id: supportTableTagId ?? '',
            });
        }

        getSupportRequests();
        setIsSubmittingTableRequests(false);

        onNextHandler?.();
        return true;
    };
    return { handleSubmit, isLoading: isSubmittingTableRequests || isLoadingSubmitRequestFlow };
};

export const useRequestPropertyFormSchema = (
    flowType: SupportInputType,
    setActiveSectionIndex: any,
) => {
    const formController = useFormContext<PropertyFormValues>();

    const supportTableTagId = useAtomValue(supportTableTagIdStore);
    const setMappedFileData = useSetAtom(mappedFileDataStore);
    const setResolverJob = useSetAtom(resolverJobStore);
    const setDataForPropertySubmit = useSetAtom(dataForPropertySubmitStore);
    const deliveryETA = useAtomValue(deliveryETAStore);
    const onNextPrepareData = useGetSubmitPropertyForm();
    const { handleSubmit: onNextTableResults, isLoading: isLoadingTableResults } =
        useHandleDoneSubmitProcess();
    const { mutateAsync: createResolverJobMutation } = useCreateResolverJobMutation();

    const { isLoading: isLoadingSubmitRequestFlow } = useSubmitRequestFlowMutation();
    const { isFetching: isLoadingResolverJobResults } = useResolverJobResultsQuery();
    const { isLoading: isLoadingUpdateResolverJob } = useUpdateResolverJobMutation();

    const handleNextPrepareData = useCallback(async () => {
        let updatedData = formController?.getValues();

        const remappedData = mapCsvColumnsToRequiredFields(updatedData, flowType);

        const currentMappedFileData: MappedFileData = {
            data: remappedData.parsedData.data,
            matching: remappedData.matching,
            id: uuidv4(),
        };

        // TODO - do we need that???
        setMappedFileData(currentMappedFileData);
        setDataForPropertySubmit({
            email: formController.getValues('email'),
            executiveEmail: formController.getValues('executiveEmail'),
            opportunityUrl: formController.getValues('opportunityUrl'),
        });

        onNextPrepareData(remappedData);

        formController.setValue('id', uuidv4());
        formController.setValue('parsedData', remappedData.parsedData);
        formController.setValue('matching', remappedData.matching);
        formController.resetField('properties'); // TODO - remove after using controller for PropertiesForm.

        setResolverJob(undefined);

        await createResolverJobMutation({ mappedFileData: currentMappedFileData });
    }, [
        formController,
        flowType,
        setMappedFileData,
        setDataForPropertySubmit,
        onNextPrepareData,
        setResolverJob,
        createResolverJobMutation,
    ]);

    const manualRequestPropertyFormSchema: FormSection<FormSectionType>[] = useMemo(
        () => [
            {
                title: 'Request Properties',
                type: 'request-properties',
                testId: 'request-properties',
                subTitle: 'Specify one or more properties to add to the platform',
                Form: PropertiesForm,
                elevioId: 850,
                nextButton: {
                    onClick: async () => {
                        handleNextPrepareData();
                    },
                },
            },
        ],
        [handleNextPrepareData],
    );
    const fileRequestPropertyFormSchema: FormSection<FormSectionType>[] = useMemo(
        () => [
            {
                title: 'Request Properties',
                type: 'request-properties',
                testId: 'request-properties',
                subTitle: 'Specify one or more properties to add to the platform',
                Form: UploadFileRequestProperty,
                elevioId: 850,
            },
            {
                title: 'Match File Data',
                testId: 'match-file-data',
                type: 'match-file-data',
                subTitle: 'Ensure that fields from your file correctly match Placer’s requirements',
                Form: FileFieldsMapping,
                elevioId: 852,
                nextButton: {
                    onClick: async () => {
                        handleNextPrepareData();
                    },
                },
            },
        ],
        [handleNextPrepareData],
    );

    const { allTableData: dataTable } = useSupportResultsTableData();
    const { statusMap, statusList } = useMemo(() => sumItemsByStatus(dataTable), [dataTable]);

    const onlySensitiveLocation = statusList.length === 1 && !!statusMap['SensitiveLocation'];
    const onlyAvailableStatus = statusList.length === 1 && !!statusMap['Available'];
    const showDoneButton = onlyAvailableStatus || onlySensitiveLocation;

    const hasEditedRows = !!statusMap['Edited'];
    const noFailedOrReviewRequired = !statusMap['ProcessingFailed'] && !statusMap['ReviewRequired'];
    const missingInfoWithoutFailedOrReview = noFailedOrReviewRequired && !!statusMap['MissingInfo'];
    const dataValidationFormShowDoneButton = noFailedOrReviewRequired && !statusMap['MissingInfo'];

    const isDataValidationSubmitDisabled =
        missingInfoWithoutFailedOrReview ||
        hasEditedRows ||
        isLoadingResolverJobResults ||
        isLoadingUpdateResolverJob ||
        isLoadingSubmitRequestFlow ||
        isLoadingTableResults;

    const { enable_support_delivery_eta_ff } = getFeatureFlags();
    const showEta =
        enable_support_delivery_eta_ff && statusMap['ReviewRequired'] < 500 && deliveryETA;

    const formsConfig: FormSection<FormSectionType>[] = useMemo(
        () => [
            ...(flowType === 'file'
                ? fileRequestPropertyFormSchema
                : manualRequestPropertyFormSchema),
            {
                title: 'Coverage Check',
                testId: 'coverage-check',
                type: 'coverage-check',
                subTitle: 'Checking which properties are available for immediate access',
                Form: SupportRequestsCoverageCheckModal,
                nextButton: {
                    isDisabled: true,
                },
                prevButton: {
                    isHidden: true,
                },
            },
            {
                title: 'Data Validation',
                testId: 'data-validation',
                type: 'data-validation',
                subTitle:
                    'See which properties are now available, or need to be revised to continue processing',
                Form: SupportRequestsResultsModal,
                elevioId: 853,
                onStepClose: ({ onNextHandler }) => {},
                prevButton: {
                    onClick: async () => {
                        setActiveSectionIndex(0);
                    },
                    preventDefault: true,
                    text: flowType === 'file' ? 'Replace File' : undefined,
                },
                nextButton: {
                    text: dataValidationFormShowDoneButton
                        ? 'Done'
                        : `${isLoadingTableResults ? 'Submitting' : 'Submit'} for Review`,
                    onClick: (props) => onNextTableResults(props),
                    isLoading: isLoadingTableResults,
                    isDisabled: isDataValidationSubmitDisabled,
                },
                extraModalProps: {
                    fullSize: true,
                },
            },
            ...((!showDoneButton
                ? [
                      {
                          title: 'Ticket Submitted',
                          testId: 'support-request-success',
                          type: 'submit',
                          Form: SupportRequestsSuccessContent,
                          formData: {
                              title: 'Thanks, We Received Your Ticket!',
                              tagId: supportTableTagId,
                              deliveryETA: showEta
                                  ? `Expected Completion by: ${format(
                                        parseISO(deliveryETA),
                                        'MMM d, yyyy',
                                    )}.`
                                  : undefined,
                              propertiesCount: statusMap['ReviewRequired'],
                              messages: [
                                  <>
                                      Our team is reviewing your{' '}
                                      <b>{statusMap['ReviewRequired']}</b>
                                      {statusMap['ReviewRequired'] === 1
                                          ? ' property'
                                          : ' properties'}{' '}
                                      and will add
                                      {statusMap['ReviewRequired'] === 1 ? ' it' : ' them'} to your
                                      account once verified.
                                  </>,
                                  <>You will receive an email once it’s complete.</>,
                              ],
                          } as SupportRequestsSuccessModalProps,
                          prevButton: {
                              isHidden: true,
                          },
                          nextButton: {
                              text: 'Close',
                          },
                      },
                  ]
                : []) as FormSection<FormSectionType>[]),
        ],
        [
            flowType,
            fileRequestPropertyFormSchema,
            manualRequestPropertyFormSchema,
            dataValidationFormShowDoneButton,
            isLoadingTableResults,
            isDataValidationSubmitDisabled,
            showDoneButton,
            supportTableTagId,
            showEta,
            deliveryETA,
            statusMap,
            setActiveSectionIndex,
            onNextTableResults,
        ],
    );

    return { formsConfig };
};
